import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, Lock, Percent, Scale } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const SCROLL_END_PX = 28;

/**
 * Disclaimer avant publication d’une annonce **vente** : qualité (3 acheteurs),
 * séquestre / litiges, sanctions — aligné sur l’esprit des CGU.
 */
export default function VenteListingDisclaimerDialog({ open, onOpenChange, onConfirm, confirmLoading }) {
  const scrollRef = useRef(null);
  const [readToEnd, setReadToEnd] = useState(false);

  const updateScrollReadState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const noScrollNeeded = scrollHeight <= clientHeight + 4;
    const atBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_END_PX;
    if (noScrollNeeded || atBottom) setReadToEnd(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setReadToEnd(false);
      return;
    }
    setReadToEnd(false);
    const id = window.requestAnimationFrame(() => {
      updateScrollReadState();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, updateScrollReadState]);

  useEffect(() => {
    if (!open || typeof ResizeObserver === "undefined") return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      updateScrollReadState();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, updateScrollReadState]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[min(92vh,820px)] w-[calc(100vw-1.5rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 shadow-lg sm:rounded-2xl",
        )}
        data-testid="vente-listing-disclaimer"
      >
        <DialogHeader className="border-b border-slate-100 bg-white px-6 pb-4 pt-5 sm:px-8 sm:pb-5 sm:pt-6">
          <div className="mb-2 flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[#2D7FF9]">
              <BadgeCheck className="h-5 w-5" aria-hidden />
            </span>
            <DialogTitle className="text-left font-display text-lg font-semibold leading-snug text-slate-900 sm:text-xl">
              Qualité de la plateforme &amp; règles pour les vendeurs
            </DialogTitle>
          </div>
          <DialogDescription className="text-left text-sm leading-relaxed text-slate-600">
            Quelques engagements clairs avant la mise en ligne — pour protéger tout le monde et éviter les abus.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={updateScrollReadState}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/50 px-6 py-4 sm:px-8 sm:py-5"
        >
          <div className="space-y-4">
            {/* Frais vendeur — annoncés ici uniquement, avant publication */}
            <div className="overflow-hidden rounded-xl border border-[#2D7FF9]/30 bg-white shadow-sm shadow-[#2D7FF9]/10">
              <div className="flex gap-4 border-b border-[#2D7FF9]/15 bg-[#2D7FF9]/5 px-4 py-3.5 sm:px-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2D7FF9]/15 text-[#2D7FF9]">
                  <Percent className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-sm font-bold text-slate-900">Frais plateforme sur vos ventes</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-700">
                    En cas de vente conclue via RdvMarket, une commission de{" "}
                    <strong className="text-slate-900">10 % HT</strong> est prélevée sur le montant de la transaction.
                    Aucun abonnement vendeur : vous ne payez que lorsqu&apos;une vente aboutit.
                  </p>
                </div>
              </div>
              <p className="px-4 py-3 text-xs leading-relaxed text-slate-500 sm:px-5">
                Le détail figure dans les conditions générales et au moment des encaissements sur la plateforme.
              </p>
            </div>

            {/* 1 — Qualité / 3 acheteurs */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
                <p className="font-display text-sm font-semibold text-slate-900">Fiabilité des annonces</p>
                <p className="mt-0.5 text-xs text-slate-600">
                  Mesure automatique pour garder une marketplace sérieuse
                </p>
              </div>
              <div className="px-4 py-4 text-sm leading-relaxed text-slate-700 sm:px-5">
                <p>
                  Afin de <strong className="text-slate-900">garantir la qualité</strong> des dossiers proposés sur
                  RdvMarket, si <strong className="text-slate-900">trois acheteurs successifs</strong> ne confirment pas le
                  rendez-vous ou refusent la qualification du dossier selon les critères affichés sur la plateforme, votre{" "}
                  <strong className="text-slate-900">annonce peut être automatiquement retirée ou désactivée</strong>.
                </p>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">
                  Détail juridique :{" "}
                  <Link to="/cgu#s4" className="font-semibold text-[#2D7FF9] underline underline-offset-2 hover:text-[#1e66e6]">
                    CGU — obligations du vendeur (§4)
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* 2 — Séquestre / litiges : rassurer */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2D7FF9]/10 text-[#2D7FF9]">
                  <Lock className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 space-y-2">
                  <p className="font-display text-sm font-bold text-slate-900">Paiements encadrés sur la plateforme</p>
                  <p className="text-sm leading-relaxed text-slate-600">
                    L’argent des acheteurs transite par nos prestataires de paiement (ex. Stripe) : les sommes restent{" "}
                    <strong className="font-semibold text-slate-800">sous contrôle de la plateforme</strong> (séquestre /
                    flux sécurisés) jusqu’à la résolution du parcours prévu — confirmation du RDV, traitement d’un litige ou
                    remboursement le cas échéant. En cas de <strong className="font-semibold text-slate-800">litige</strong>
                    , les versements peuvent être <strong className="font-semibold text-slate-800">suspendus</strong>{" "}
                    jusqu’à décision.
                  </p>
                  <p className="text-xs leading-relaxed text-slate-500">
                    <Link to="/cgu#s6" className="font-semibold text-[#2D7FF9] underline underline-offset-2 hover:text-[#1e66e6]">
                      CGU — paiement et séquestre (§6)
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 3 — Fraude / sanctions */}
            <div className="rounded-xl border border-red-200 bg-red-50/80 p-4 sm:p-5">
              <div className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-800">
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 space-y-2">
                  <p className="font-display text-sm font-bold text-red-950">Fraudes, arnaques et fausses annonces</p>
                  <p className="text-sm leading-relaxed text-red-950/95">
                    Toute <strong>annonce trompeuse</strong>, tentative d’<strong>escroquerie</strong> (faux dossiers,
                    rendez-vous fictifs, détournement des canaux obligatoires…) ou manœuvre visant à tromper un acheteur
                    expose à des mesures sévères : <strong>remboursement des victimes</strong>,{" "}
                    <strong>clôture du compte</strong>, <strong>coopération avec les autorités</strong> et, le cas
                    échéant, <strong>poursuites civiles ou pénales</strong>, sans préjudice de ce que prévoient les{" "}
                    <strong>conditions générales</strong>.
                  </p>
                  <div className="flex items-start gap-2 rounded-xl border border-red-200/80 bg-white/90 px-3 py-2.5 text-xs leading-relaxed text-red-950/90">
                    <Scale className="mt-0.5 h-4 w-4 shrink-0 text-red-700/80" aria-hidden />
                    <span>
                      La plateforme documente les échanges (appels, journaux techniques). Les litiges sont traités selon
                      les règles publiées ; en cas de fraude avérée ou répétée, un{" "}
                      <strong>bannissement définitif</strong> peut être prononcé.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex w-full flex-col gap-3 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3 sm:px-8 sm:py-5">
          {!readToEnd && !confirmLoading ? (
            <p className="w-full basis-full text-center text-xs leading-snug text-slate-500 sm:text-right">
              Faites défiler jusqu’en bas du texte pour activer la publication.
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-xl border-slate-200 sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={confirmLoading}
          >
            Annuler
          </Button>
          <Button
            type="button"
            className="btn-primary h-11 w-full rounded-lg px-6 font-semibold sm:w-auto disabled:opacity-50"
            onClick={onConfirm}
            disabled={confirmLoading || !readToEnd}
            data-testid="vente-disclaimer-confirm"
            aria-disabled={confirmLoading || !readToEnd}
          >
            {confirmLoading ? "Publication…" : "J’ai lu — publier l’annonce"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
